/**
 * Elasticsearch Credential Form
 *
 * A form for configuring Elasticsearch credentials with:
 * - Base URL configuration
 * - Authentication options (Basic Auth or API Key)
 * - SSL/TLS settings
 */

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

export interface ElasticsearchFormData {
  // Connection
  baseUrl: string;

  // Authentication Type
  authType: 'basic' | 'apiKey';

  // Basic Auth
  username: string;
  password: string;

  // API Key Auth
  apiKey: string;

  // SSL Settings
  ignoreSslIssues: boolean;
}

interface ElasticsearchCredentialFormProps {
  formData: ElasticsearchFormData;
  onChange: (data: Partial<ElasticsearchFormData>) => void;
  isEditing?: boolean;
  testResult?: { success: boolean; message: string; details?: any } | null;
  testing?: boolean;
  onTest?: () => void;
}

export function ElasticsearchCredentialForm({
  formData,
  onChange,
  isEditing = false,
}: ElasticsearchCredentialFormProps) {
  const handleChange = (field: keyof ElasticsearchFormData, value: any) => {
    onChange({ [field]: value });
  };

  return (
    <div className="space-y-4">
      {/* Base URL */}
      <div className="space-y-2">
        <Label htmlFor="baseUrl">
          Base URL <span className="text-red-500">*</span>
        </Label>
        <Input
          id="baseUrl"
          placeholder="http://localhost:9200"
          value={formData.baseUrl}
          onChange={(e) => handleChange('baseUrl', e.target.value)}
          className="font-mono text-sm"
        />
      </div>

      {/* Authentication Type */}
      <div className="space-y-3">
        <Label>Authentication</Label>
        <RadioGroup
          value={formData.authType || 'basic'}
          onValueChange={(value) => handleChange('authType', value)}
          className="grid grid-cols-2 gap-4"
        >
          <div className="flex items-center space-x-2 rounded-lg border p-3">
            <RadioGroupItem value="basic" id="auth-basic" />
            <Label htmlFor="auth-basic" className="cursor-pointer font-normal">
              Basic Auth
            </Label>
          </div>
          <div className="flex items-center space-x-2 rounded-lg border p-3">
            <RadioGroupItem value="apiKey" id="auth-apikey" />
            <Label htmlFor="auth-apikey" className="cursor-pointer font-normal">
              API Key
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Basic Auth Fields */}
      {(formData.authType === 'basic' || !formData.authType) && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              placeholder="elastic"
              value={formData.username}
              onChange={(e) => handleChange('username', e.target.value)}
              autoComplete="off"
              className="font-mono text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder={isEditing ? "Leave unchanged or enter new" : "Enter password"}
              value={formData.password}
              onChange={(e) => handleChange('password', e.target.value)}
              autoComplete="new-password"
              className="font-mono text-sm"
            />
          </div>
        </div>
      )}

      {/* API Key Field */}
      {formData.authType === 'apiKey' && (
        <div className="space-y-2">
          <Label htmlFor="apiKey">
            API Key <span className="text-red-500">*</span>
          </Label>
          <Input
            id="apiKey"
            type="password"
            placeholder={isEditing ? "Leave unchanged or enter new" : "Enter API key"}
            value={formData.apiKey}
            onChange={(e) => handleChange('apiKey', e.target.value)}
            autoComplete="new-password"
            className="font-mono text-sm"
          />
        </div>
      )}

      {/* SSL Settings */}
      <div className="flex items-center justify-between rounded-lg border p-3">
        <div className="space-y-0.5">
          <Label htmlFor="ignoreSslIssues">Ignore SSL Issues</Label>
          <p className="text-xs text-muted-foreground">
            Skip SSL certificate validation
          </p>
        </div>
        <Switch
          id="ignoreSslIssues"
          checked={formData.ignoreSslIssues}
          onCheckedChange={(checked) => handleChange('ignoreSslIssues', checked)}
        />
      </div>
    </div>
  );
}
